import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface Props {
  /** Changing this (the route's path) clears a caught error, so navigating away recovers. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Catches a route that failed to render, above all a lazy route whose chunk would
 * not download. A deploy replaces the hashed chunk files, so a tab opened before
 * it asks for files that are gone, and without a boundary React unmounts the whole
 * app to a white page. This keeps the shell and says what to do instead.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('A page failed to render:', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <Box role="alert" sx={{ p: 3, maxWidth: 560 }}>
        <Typography component="h1" sx={{ fontSize: '1.125rem', fontWeight: 600, mb: 0.5 }}>
          This page couldn&apos;t be loaded
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
          Teamlane may have been updated since you opened it. Reload to get the latest version.
        </Typography>
        <Button variant="contained" size="small" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    );
  }
}

export default RouteErrorBoundary;
